import React, { useEffect } from 'react';
import { useCounter } from '../Home/Home';

const leftSteps = [
  { num:'01', title:'Campus Identification',  desc:'Mapping the right institutions aligned with your brand values and target demographics.' },
  { num:'02', title:'Initial Assessment',     desc:'Evaluating campus culture, student profile, and potential campaign touchpoints.' },
  { num:'03', title:'Planning Phase',         desc:'Building a tailored roadmap with timelines, resources, and measurable objectives.' },
];

const rightSteps = [
  { num:'04', title:'Active Campaigns',       desc:'Deploying on-ground activations, events, and promotional initiatives with precision.' },
  { num:'05', title:'Student Engagement',     desc:'Creating genuine interactions through ambassadors, workshops, and interactive experiences.' },
  { num:'06', title:'Long-term Partnership',  desc:'Sustaining relationships that grow brand presence semester after semester.' },
];

const stats = [
  { num:'50',   suffix:'+', label:'Campus Events',          color:'var(--red)'  },
  { num:'120',  suffix:'+', label:'Promotional Campaigns',  color:'var(--blue)' },
  { num:'10000',suffix:'+', label:'Student Reach',          color:'var(--red)'  },
];

function StatPill({ num, suffix, label, color }) {
  const ref = React.useRef(null);
  const [inView, setInView] = React.useState(false);
  React.useEffect(() => {
    const io = new IntersectionObserver(([e]) => { if (e.isIntersecting) setInView(true); }, { threshold: 0.5 });
    if (ref.current) io.observe(ref.current);
    return () => io.disconnect();
  }, []);
  const val = useCounter(num, inView);
  return (
    <div ref={ref} className="card p-7 text-center red-hover">
      <div className="stat-num" style={{ color }}>{val.toLocaleString()}{suffix}</div>
      <p className="mt-2 text-white/50 text-sm">{label}</p>
    </div>
  );
}

function StepList({ steps, accentColor, iconBg }) {
  return (
    <div className="flex flex-col gap-6">
      {steps.map((s, i) => (
        <div key={s.num} className="process-step flex items-start gap-4">
          <div
            className="w-12 h-12 rounded-xl flex items-center justify-center shrink-0 font-bold text-sm"
            style={{ background: accentColor === 'red' ? 'var(--red-dim)' : 'var(--blue-dim)',
                     border: `1px solid ${accentColor === 'red' ? 'rgba(255,0,50,0.25)' : 'rgba(5,131,242,0.25)'}`,
                     color: accentColor === 'red' ? 'var(--red)' : 'var(--blue)' }}
          >
            {s.num}
          </div>
          <div className="pt-1">
            <h4 className="text-white font-semibold text-sm mb-1">{s.title}</h4>
            <p className="text-white/45 text-xs leading-relaxed">{s.desc}</p>
          </div>
        </div>
      ))}
    </div>
  );
}

export default function CampusCharter() {
  useEffect(() => {
    window.scrollTo(0, 0);
    const nodes = document.querySelectorAll('.reveal, .reveal-l, .reveal-r');
    const io = new IntersectionObserver(
      entries => entries.forEach(e => { if (e.isIntersecting) e.target.classList.add('in'); }),
      { threshold: 0.1 }
    );
    nodes.forEach(n => io.observe(n));
    return () => io.disconnect();
  }, []);

  return (
    <>
      {/* Hero */}
      <section
        className="relative w-full py-28 px-6 flex items-center justify-center text-center overflow-hidden"
        style={{ background:'linear-gradient(160deg,#141414 60%,#1c1010 100%)' }}
      >
        <div className="absolute bottom-0 left-0 w-[400px] h-[300px] pointer-events-none"
          style={{ background:'radial-gradient(ellipse at 0% 100%,rgba(255,0,50,.1),transparent 70%)' }} />
        <div className="absolute top-0 right-0 w-[400px] h-[300px] pointer-events-none"
          style={{ background:'radial-gradient(ellipse at 100% 0%,rgba(5,131,242,.08),transparent 70%)' }} />

        <div className="relative z-10 max-w-2xl mx-auto">
          <span className="label label-red mb-6 inline-block">Separate Programme</span>
          <h1 className="heading" style={{ fontSize:'clamp(2.5rem,6vw,4rem)' }}>
            Campus <em>Charter</em>
          </h1>
          <div className="divider center mt-5 mb-6" />
          <p className="text-white/55 text-lg leading-relaxed">
            A structured programme that takes brands from initial campus identification
            all the way to long-term student partnerships — step by step.
          </p>
        </div>
      </section>

      {/* Process */}
      <section className="section" style={{ background:'#161616' }}>
        <div className="max-w-content mx-auto">
          <div className="text-center mb-14 reveal">
            <span className="label label-blue">The Process</span>
            <h2 className="heading mt-4">How <em style={{ color:'var(--blue)' }}>It Works</em></h2>
            <div className="divider blue center" />
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-10">
            <div className="card p-8 accent-red red-hover reveal-l">
              <h3 className="text-white font-bold text-base mb-6 flex items-center gap-3">
                <span className="w-6 h-6 rounded flex items-center justify-center text-xs font-bold"
                  style={{ background:'var(--red-dim)', color:'var(--red)', border:'1px solid rgba(255,0,50,0.25)' }}>1</span>
                Foundation
              </h3>
              <StepList steps={leftSteps} accentColor="red" />
            </div>

            <div className="card p-8 accent-blue blue-hover reveal-r">
              <h3 className="text-white font-bold text-base mb-6 flex items-center gap-3">
                <span className="w-6 h-6 rounded flex items-center justify-center text-xs font-bold"
                  style={{ background:'var(--blue-dim)', color:'var(--blue)', border:'1px solid rgba(5,131,242,0.25)' }}>2</span>
                Execution
              </h3>
              <StepList steps={rightSteps} accentColor="blue" />
            </div>
          </div>
        </div>
      </section>

      {/* Stats */}
      <section className="section" style={{ background:'#141414' }}>
        <div className="max-w-content mx-auto">
          <div className="text-center mb-12 reveal">
            <span className="label label-red">Track Record</span>
            <h2 className="heading mt-4">Campus Charter <em>in Numbers</em></h2>
            <div className="divider center" />
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-6">
            {stats.map(s => <StatPill key={s.label} {...s} />)}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="section" style={{ background:'#161616' }}>
        <div className="max-w-content mx-auto">
          <div className="card p-12 text-center reveal"
            style={{ borderColor:'rgba(255,0,50,0.15)', background:'#1e1212' }}>
            <span className="label label-red mb-5 inline-block">Join the Programme</span>
            <h2 className="heading mb-4">Ready to Charter<br /><em>Your Campus Journey?</em></h2>
            <p className="text-white/50 text-base max-w-md mx-auto mb-10">
              Get in touch and we'll walk you through how Campus Charter can work for your institution or brand.
            </p>
            <a href="/#contact" className="btn-red">Start the Conversation</a>
          </div>
        </div>
      </section>
    </>
  );
}
